import clsx from 'clsx';
import type { DragEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Plan } from '@/domain/types';
import {
  getDisplayTitle,
  planChipSurfaceStyle,
  resolveOwnerAccentColor,
} from '@/domain/plans';
import {
  formatPlanTimeLabel,
  formatPlanTimeLabelCompact,
  resolvePlanAuthorLabel,
} from '@/features/plans/planChipMeta';
import { useAppStore } from '@/app/store';
import styles from './PlanChip.module.css';

export interface PlanChipProps {
  plan: Plan;
  onClick?: () => void;
  draggable?: boolean;
  compact?: boolean;
  grid?: boolean;
  shared?: boolean;
  className?: string;
  onDragStart?: (event: DragEvent<HTMLButtonElement>) => void;
  /**
   * Month grid mobile: title → time → nickname stacked.
   * Prefer this (or container query) over squeezing desktop row into 50px.
   */
  mobileStack?: boolean;
  /** Prefer compact time ranges (05–14). */
  compactTime?: boolean;
}

export function PlanChip({
  plan,
  onClick,
  draggable = false,
  compact = false,
  grid = false,
  shared = false,
  className,
  onDragStart,
  mobileStack = false,
  compactTime = false,
}: PlanChipProps) {
  const { t } = useTranslation();
  const session = useAppStore((s) => s.session);
  const profiles = useAppStore((s) => s.profiles);
  const preferences = useAppStore((s) => s.preferences);
  const openPlanEditor = useAppStore((s) => s.openPlanEditor);

  const viewerId = session?.user.id ?? '';
  const timeFormat = preferences?.timeFormat ?? '24h';
  const ownerAccent = resolveOwnerAccentColor(plan, profiles, viewerId);
  const surface = planChipSurfaceStyle(ownerAccent);
  const title = getDisplayTitle(plan, viewerId, t('availability.busy'));
  const timeLabel = compactTime
    ? formatPlanTimeLabelCompact(plan, timeFormat, t('plan.allDay'))
    : formatPlanTimeLabel(plan, timeFormat, t('plan.allDay'));
  const authorLabel = resolvePlanAuthorLabel(plan, profiles);
  const fullTime = formatPlanTimeLabel(plan, timeFormat, t('plan.allDay'));
  const tooltip = [title, fullTime, authorLabel].filter(Boolean).join(' · ');

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (onClick) {
      onClick();
      return;
    }
    openPlanEditor(plan.id);
  };

  return (
    <button
      type="button"
      className={clsx(
        styles.chip,
        compact && styles.compact,
        grid && styles.gridChip,
        grid && mobileStack && styles.gridChipMobile,
        shared && styles.shared,
        className,
      )}
      style={surface}
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={handleClick}
      title={tooltip}
      aria-label={tooltip}
    >
      <span className={styles.rail} aria-hidden />
      <span className={styles.content}>
        <span className={styles.title}>{title}</span>
        <span className={styles.meta}>
          {timeLabel ? <span className={clsx(styles.time, 'mono')}>{timeLabel}</span> : null}
          {authorLabel ? (
            <span className={styles.author}>{authorLabel}</span>
          ) : (
            <span className={styles.authorSkeleton} aria-hidden />
          )}
        </span>
      </span>
    </button>
  );
}
